import moment from "moment";

import * as actionTypes from "../../../constants";
import { mapChoiceListsToObject } from "../../../reducers/choiceLists";
import { encounterAPI } from "../../../services/encounter";
import { parseServiceErrors } from "../../../services/encounter/serviceErrors";
import { getRecoveredEncountersCountAction } from "../../RecoveredEncounters/actions/recoveredEncountersAction";

import { getEncounterAdditionalLists } from "./encounterAdditionalLists";
import { applyFacilityData, getFacilityData } from "./facilityChange";
import { prepareToOpenEncounter } from "./prepareToOpenEncounter";

import type { AnyAction } from "redux";
import type { ThunkAction } from "redux-thunk";
import type { EncounterEntity } from "../../../models";
import type { CreateEncounterState } from "../../../models/createEncounter";
import type { FacilityData, FacilityPreferences } from "../../../models/facilitySettings";
import type { PartialChoiceLists } from "../../../models/patientEncounter";
import type { ErrorDetails } from "../../../models/ui";
import type { State } from "../../../reducers";

export const createEncounterAction =
  (
    newEncounterData: CreateEncounterState,
    codeMetadataInitialized: boolean,
  ): ThunkAction<Promise<boolean>, State, unknown, AnyAction> =>
  async (dispatch) => {
    dispatch(createEncounterBegin());

    return prepareToOpenEncounter(dispatch, codeMetadataInitialized)
      .then((facilities) =>
        encounterAPI
          .createEncounter(newEncounterData)
          .then((createResult) => {
            const { facility, type } = createResult;
            dispatch(getRecoveredEncountersCountAction());

            if (!facility) {
              // We have not selected facility - show error
              const errors = [
                { error: "No Facility", description: "No Facility", time: moment.now() },
              ];
              dispatch(createEncounterError(errors));

              return false;
            }

            const interfaceMode = createResult.IsInterfaced || false;

            return getFacilityData(facility, type, interfaceMode, facilities, createResult).then(
              (facilityData: FacilityData) => {
                if (!applyFacilityData(dispatch, facility, facilityData)) {
                  dispatch(createEncounterError([]));
                  return false;
                }

                if (facilityData) {
                  if (facilityData.partialChoiceLists && facilityData.choiceListsData) {
                    const choiceListsData = {};
                    mapChoiceListsToObject(choiceListsData, facilityData.choiceListsData);
                    dispatch(
                      createEncounterCompleted(
                        createResult,
                        choiceListsData,
                        facilityData.partialChoiceLists,
                        facilityData.preferences,
                      ),
                    );

                    return true;
                  }
                }

                dispatch(createEncounterError([]));
                return false;
              },
            );
          })
          .then((result) => {
            if (!result) {
              return false;
            }

            return dispatch(getEncounterAdditionalLists()).then(() => true);
          }),
      )
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Cannot Create Encounter");

        dispatch(createEncounterError(errors));

        return false;
      });
  };

const createEncounterBegin = () => ({
  payload: "",
  type: actionTypes.CREATE_ENCOUNTER_BEGIN,
});

const createEncounterCompleted = (
  encounterEntity: EncounterEntity,
  choiceListsData,
  partialChoiceLists: PartialChoiceLists,
  facilityPreferences?: FacilityPreferences,
) => ({
  // always open Patient Encounter tab for the new encounter
  payload: {
    encounterEntity,
    choiceLists: choiceListsData,
    partialChoiceLists,
    facilityPreferences,
    openEncounterToCodingTab: false,
  },
  type: actionTypes.CREATE_ENCOUNTER_COMPLETED,
});

const createEncounterError = (errors: ErrorDetails[]) => ({
  payload: errors,
  type: actionTypes.CREATE_ENCOUNTER_ERROR,
});
